// BRIEF 122 §2 — "WHO SHOULD ATTEND?", THE ONE LIST.
//
// Laurent, glancing 2a-i: "for legibility, the New Event modal should show ONE
// list named 'Who should attend?' listing all possible candidates with
// checkboxes, with subdivisions: Case Team members (pre checked, modifiable) +
// the categories in Parties' pane."
//
// WHAT IT REPLACES, and what each of the three had to keep contributing:
//
//   MultiSelect "Should attend"   the firm's whole choosable roster, and the
//                                 write to `assignedCollaborators`.
//   EventPeopleOverview           the response status, and the "as of" caption
//                                 that bounds it.
//   AttendeesPickerSection        the checkboxes and the sectioning.
//
// NOTHING DISPLAYED TODAY STOPS BEING DISPLAYED — §2's rule, and the reason
// this renders a checkbox, a name, an address AND a status chip on one row
// rather than picking two of the three. The status rides the row; the
// timestamp is a caption on the whole photograph and stays a caption, because
// that is what it always was — it says when the calendar was last read, which
// is not a fact about any one person.
//
// IT DOES NOT DECIDE A POPULATION, A SECTION OR A CHECK. All three are
// `mergeAttendeeRows`' (src/lib/meetingAttendees.ts), for 119 §5's own reason:
// a producer groups a population it is handed. This renders what it is given,
// in the order it is given, which is why the parties-pane order arrives here
// without anything sorting it.
import type { JSX } from "react";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import ResponseChip from "./ResponseChip";
import type { AttendeeRow } from "../lib/meetingAttendees";

export default function EventAttendeesSection({
  title,
  rows,
  onToggle,
  at,
  locale,
}: {
  /** §2 "Who should attend?" · §3a "Remove or invite participants". */
  title: string;
  rows: readonly AttendeeRow[];
  onToggle: (key: string) => void;
  /** `AgendaEvent.outlookAttendeesAt` — null when the calendar was never read. */
  at?: string | null;
  locale?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (!rows || !rows.length) return null;

  /* Sections in FIRST-APPEARANCE order, which is the merge's order, which is
     the parties pane's own. A row arriving late — a colleague resolved off the
     event rather than out of the cast — joins the section already open for his
     group instead of starting a second one with the same heading. */
  const sections: { label: string; people: AttendeeRow[] }[] = [];
  const at_ = new Map<string, { label: string; people: AttendeeRow[] }>();
  for (const r of rows) {
    let s = at_.get(r.group);
    if (!s) {
      s = { label: r.group, people: [] };
      at_.set(r.group, s);
      sections.push(s);
    }
    s.people.push(r);
  }

  return (
    <div className="field" style={{ marginBottom: 8 }}>
      <label
        style={{
          fontSize: FS.s10,
          fontWeight: 600,
          color: "var(--tp)",
          marginBottom: 6,
          display: "block",
        }}
      >
        {title}
      </label>
      {sections.map((s) => (
        <div key={s.label} style={{ marginBottom: 6 }}>
          <span
            style={{
              fontSize: FS.s9,
              color: "var(--tm)",
              textTransform: "uppercase",
              letterSpacing: ".3px",
              fontWeight: 600,
            }}
          >
            {s.label}
          </span>
          {s.people.map((r) => (
            <label
              key={r.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "3px 0",
                fontSize: FS.s10_5,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={r.checked}
                onChange={() => onToggle(r.key)}
              />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={r.email || r.name}
              >
                {r.name}
              </span>
              <span
                style={{
                  color: "var(--tm)",
                  fontSize: FS.s9_5,
                  flexShrink: 0,
                }}
              >
                {r.email}
              </span>
              <ResponseChip response={r.response} />
            </label>
          ))}
        </div>
      ))}
      {/* A TIMESTAMP, NOT A PROMISE — 119 §6's words, and the caption survives
          the merge unchanged. Rendered only when the calendar has actually been
          read: an event nobody has looked at yet says nothing here rather than
          implying a look that never happened. */}
      {at && (
        <div
          style={{
            fontSize: FS.s9_5,
            color: "var(--tm)",
            marginTop: 2,
            borderTop: "1px solid var(--br)",
            paddingTop: 4,
          }}
        >
          {t("people.response.as_of", {
            when: new Date(at).toLocaleString(locale || undefined),
          })}
        </div>
      )}
    </div>
  );
}
